import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * RealHRISIntegrationPanel
 * --------------------------------------------------------------------------
 * Demonstrates ClearEqual's HRIS integration story with REAL HTTP fetches.
 *
 * Two parts:
 *   1. Provider grid, Workday, SuccessFactors, BambooHR, Personio, ADP,
 *      Merge.dev. Each card shows a Connect button.
 *   2. Live demo, clicks "Fetch sample employees" → calls a real public
 *      API (DummyJSON's users endpoint) → shows loading state → renders
 *      the returned employee list. This is the same fetch shape a real
 *      Workday or Merge.dev integration uses; only the URL and auth token
 *      differ.
 *
 * Architecture diagram beneath explains how OAuth, token vault, and
 * sync schedule fit together in production.
 *
 * NOTE: in production the auth flow goes:
 *   browser → /api/integration/connect (Vercel function)
 *           → provider OAuth consent screen
 *           → callback stores encrypted token in EU-region vault
 *           → background worker syncs via authenticated API calls
 * The browser NEVER holds the customer's HRIS API key.
 */

const PROVIDERS = [
  { id: 'workday',        name: 'Workday',          descKey: 'integration.providerDesc.workday',        color: '#005EB8', initials: 'W',  available: true },
  { id: 'successfactors', name: 'SAP SuccessFactors', descKey: 'integration.providerDesc.successfactors', color: '#0066B3', initials: 'SF', available: true },
  { id: 'merge',          name: 'Merge.dev',        descKey: 'integration.providerDesc.merge',          color: '#3B82F6', initials: 'M',  available: true, recommended: true },
  { id: 'personio',       name: 'Personio',         descKey: 'integration.providerDesc.personio',       color: '#3D5468', initials: 'P',  available: true },
  { id: 'bamboohr',       name: 'BambooHR',         descKey: 'integration.providerDesc.bamboohr',       color: '#73C41D', initials: 'B',  available: true },
  { id: 'adp',            name: 'ADP Workforce Now', descKey: 'integration.providerDesc.adp',            color: '#D71E28', initials: 'A',  available: false },
];

const RealHRISIntegrationPanel = () => {
  const { t } = useTranslation();
  const [connectingId, setConnectingId] = useState(null);
  const [connectedIds, setConnectedIds] = useState(new Set());
  const [employees, setEmployees] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Connect handler, for the demo, this simulates the OAuth round-trip
   * with a 1.5s spinner. In production this would open the provider's
   * consent screen, await the redirect, and store the token server-side.
   */
  const handleConnect = (providerId) => {
    setConnectingId(providerId);
    setTimeout(() => {
      setConnectedIds((prev) => new Set([...prev, providerId]));
      setConnectingId(null);
    }, 1500);
  };

  const handleDisconnect = (providerId) => {
    setConnectedIds((prev) => {
      const next = new Set(prev);
      next.delete(providerId);
      return next;
    });
  };

  /**
   * Live fetch, calls a real public API to show actual data flow. This
   * uses DummyJSON's /users endpoint which returns realistic-looking
   * employee profiles with name, email, company, role, salary etc.
   *
   * The shape of this code is identical to a real production integration:
   *   1. setIsFetching(true)
   *   2. fetch() the API endpoint
   *   3. parse JSON
   *   4. transform into ClearEqual's employee model
   *   5. render
   *
   * Only the auth header and URL differ in production.
   */
  const handleFetchEmployees = async () => {
    setIsFetching(true);
    setFetchError('');
    try {
      const response = await fetch('https://dummyjson.com/users?limit=15&select=firstName,lastName,email,phone,company,address');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      // Transform to ClearEqual's internal employee shape
      const transformed = (data?.users || []).map((u) => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
        email: u.email,
        phone: u.phone,
        role: u.company?.title || 'Employee',
        department: u.company?.department || 'N/A',
        location: u.address?.city ? `${u.address.city}, ${u.address.country}` : 'N/A',
      }));
      setEmployees(transformed);
    } catch (err) {
      console.error('Fetch failed', err);
      setFetchError(t('integration.fetchError'));
    } finally {
      setIsFetching(false);
    }
  };

  const filteredEmployees = searchTerm
    ? employees.filter((e) => {
        const haystack = `${e.name} ${e.role} ${e.department}`.toLowerCase();
        return haystack.includes(searchTerm.toLowerCase());
      })
    : employees;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">{t('integration.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('integration.subtitle')}</p>
      </div>

      {/* Provider grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PROVIDERS.map((p) => {
          const isConnecting = connectingId === p.id;
          const isConnected = connectedIds.has(p.id);
          return (
            <div
              key={p.id}
              className={`relative bg-card border rounded-lg p-5 shadow-soft transition-all ${
                isConnected ? 'border-success/40 ring-1 ring-success/20' : 'border-border'
              }`}
            >
              {p.recommended && (
                <span className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-primary text-primary-foreground">
                  {t('integration.recommendedBadge')}
                </span>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: p.color }}
                >
                  {p.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground">{p.name}</p>
                  {!p.available && (
                    <p className="text-xs text-muted-foreground italic">{t('integration.planComingSoon')}</p>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4 leading-snug">{t(p.descKey)}</p>
              {isConnected ? (
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success border border-success/20">
                    <Icon name="CheckCircle2" size={12} />
                    {t('integration.connectedBadge')}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDisconnect(p.id)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t('integration.disconnect')}
                  </button>
                </div>
              ) : (
                <Button
                  variant={p.recommended ? 'default' : 'outline'}
                  size="sm"
                  fullWidth
                  iconName={isConnecting ? 'Loader' : 'Link'}
                  iconPosition="left"
                  onClick={() => handleConnect(p.id)}
                  disabled={!p.available || isConnecting}
                >
                  {isConnecting ? t('integration.connectingBtn') : t('integration.connectBtn')}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Live API demo */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Radio" size={18} className="text-primary" />
              <h3 className="font-semibold text-foreground">{t('integration.liveConnectionDemo')}</h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary border border-primary/20">
                {t('integration.realConnectorBadge')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{t('integration.liveConnectionDemoBody')}</p>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName={isFetching ? 'Loader' : 'Download'}
            iconPosition="left"
            onClick={handleFetchEmployees}
            disabled={isFetching}
          >
            {isFetching ? t('integration.fetching') : t('integration.fetchEmployees')}
          </Button>
        </div>

        {fetchError && (
          <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm text-destructive flex items-start gap-2 mb-4">
            <Icon name="AlertCircle" size={16} className="shrink-0 mt-0.5" />
            <span>{fetchError}</span>
          </div>
        )}

        {employees.length > 0 && (
          <>
            <div className="bg-success/10 border border-success/20 rounded p-2 mb-4 flex items-center gap-2 text-sm text-success">
              <Icon name="CheckCircle2" size={14} />
              <span>{t('integration.fetchSuccess', { count: employees.length })}</span>
            </div>

            <div className="mb-3">
              <input
                type="text"
                placeholder={t('integration.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{t('integration.tableName')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{t('integration.tableRole')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground hidden md:table-cell">{t('integration.tableDepartment')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground hidden lg:table-cell">{t('integration.tableLocation')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredEmployees.map((e) => (
                    <tr key={e.id} className="hover:bg-muted/20">
                      <td className="px-3 py-2 text-foreground">{e.name}</td>
                      <td className="px-3 py-2 text-foreground">{e.role}</td>
                      <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{e.department}</td>
                      <td className="px-3 py-2 text-muted-foreground hidden lg:table-cell">{e.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Architecture diagram */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center gap-2 mb-5">
          <Icon name="GitBranch" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">{t('integration.archDiagramTitle')}</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0">
                {step}
              </div>
              <div className="flex-1 pb-3 border-b border-border last:border-b-0 last:pb-0">
                <p className="font-semibold text-sm text-foreground">{t(`integration.archStep${step}`)}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{t(`integration.archStep${step}Body`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RealHRISIntegrationPanel;
